"use client";
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, Filter, Plus, Edit3, Trash2, Grid3X3, ChevronDown, Layers, Tag, Table, CheckCircle } from 'lucide-react';
import ModalModal from './ModalModal';
import { useModels } from '../context/ModelContext';
import { useCategories } from '../context/CategoryContext';
import { useSubCategories } from '../context/SubcategoryContext';

const ModalPage = () => {
  const { models, setModels, toggleStatus, updateModel, addModel, deleteModel } = useModels();
  const { categories, fetchCategories } = useCategories();
  const { subCategories, fetchSubCategories } = useSubCategories();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subCategoryFilter, setSubCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedModals, setSelectedModals] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingModal, setEditingModal] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (!e.target.closest('[data-dropdown]')) setOpenDropdown(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    fetchCategories().catch((err) => console.warn('Failed to fetch categories', err));
    fetchSubCategories().catch((err) => console.warn('Failed to fetch sub-categories', err));
  }, [fetchCategories, fetchSubCategories]);

  const showToast = useCallback((message = 'Done!', type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  }, []);

  // ── Effective status (respects parent inactive state) ──────────────────────
  const getEffectiveStatus = useCallback((modal) => {
    const cat = categories.find((c) => String(c.id) === String(modal.category_id));
    const sub = subCategories.find((s) => String(s.id) === String(modal.sub_category_id));
    return cat?.status === 'Inactive' || sub?.status === 'Inactive' ? 'Inactive' : modal.status;
  }, [categories, subCategories]);

  const isParentInactive = useCallback((modal) => {
    const cat = categories.find((c) => String(c.id) === String(modal.category_id));
    const sub = subCategories.find((s) => String(s.id) === String(modal.sub_category_id));
    if (cat?.status === 'Inactive') return 'category';
    if (sub?.status === 'Inactive') return 'subcategory';
    return null;
  }, [categories, subCategories]);

  // ── Filter / sort options ──────────────────────────────────────────────────
  const statusOptions = useMemo(() => [
    { value: 'all',      label: 'All Models', count: models.length },
    { value: 'Active',   label: 'Active',     count: models.filter((m) => getEffectiveStatus(m) === 'Active').length },
    { value: 'Inactive', label: 'Inactive',   count: models.filter((m) => getEffectiveStatus(m) === 'Inactive').length },
  ], [models, getEffectiveStatus]);

  const categoryOptions = useMemo(() => {
    const base = statusFilter === 'all' ? models : models.filter((m) => getEffectiveStatus(m) === statusFilter);
    const opts = categories
      .map((cat) => ({ value: cat.id, label: cat.name, count: base.filter((m) => String(m.category_id) === String(cat.id)).length }))
      .filter((o) => o.count > 0);
    return [{ value: 'all', label: 'All Categories', count: base.length }, ...opts];
  }, [categories, models, statusFilter, getEffectiveStatus]);

  const subCategoryOptions = useMemo(() => {
    const base = statusFilter === 'all' ? models : models.filter((m) => getEffectiveStatus(m) === statusFilter);
    const catBase = categoryFilter === 'all' ? base : base.filter((m) => String(m.category_id) === String(categoryFilter));
    const relevantSubs = categoryFilter === 'all' ? subCategories : subCategories.filter((s) => String(s.category_id) === String(categoryFilter));
    const opts = relevantSubs
      .map((sub) => ({ value: sub.id, label: sub.subcat_name, count: catBase.filter((m) => String(m.sub_category_id) === String(sub.id)).length }))
      .filter((o) => o.count > 0);
    return [{ value: 'all', label: 'All Sub-Categories', count: catBase.length }, ...opts];
  }, [subCategories, models, statusFilter, categoryFilter, getEffectiveStatus]);

  const sortOptions = [
    { value: 'name',        label: 'Name A–Z' },
    { value: 'name-desc',   label: 'Name Z–A' },
    { value: 'category',    label: 'Category' },
    { value: 'subcategory', label: 'Sub-Category' },
    { value: 'newest',      label: 'Newest First' },
    { value: 'oldest',      label: 'Oldest First' },
  ];

  // Auto-reset dependent filters when options disappear
  useEffect(() => {
    if (categoryFilter !== 'all' && !categoryOptions.some((o) => String(o.value) === String(categoryFilter))) {
      setCategoryFilter('all');
      setSubCategoryFilter('all');
    }
  }, [categoryOptions, categoryFilter]);

  useEffect(() => {
    if (subCategoryFilter !== 'all' && !subCategoryOptions.some((o) => String(o.value) === String(subCategoryFilter))) {
      setSubCategoryFilter('all');
    }
  }, [subCategoryOptions, subCategoryFilter]);

  // ── Filtered + sorted list ─────────────────────────────────────────────────
  const filteredAndSortedModals = useMemo(() => {
    const parseDate = (v) => { if (!v) return 0; const t = new Date(v.replace(' ', 'T')).getTime(); return isNaN(t) ? 0 : t; };

    return models
      .filter((m) => {
        const q = searchTerm.toLowerCase();
        return (
          (!q || m.model_name?.toLowerCase().includes(q) || m.sub_category_name?.toLowerCase().includes(q) || m.category_name?.toLowerCase().includes(q)) &&
          (statusFilter === 'all' || getEffectiveStatus(m) === statusFilter) &&
          (categoryFilter === 'all' || String(m.category_id) === String(categoryFilter)) &&
          (subCategoryFilter === 'all' || String(m.sub_category_id) === String(subCategoryFilter))
        );
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name':        return (a.model_name || '').localeCompare(b.model_name || '');
          case 'name-desc':   return (b.model_name || '').localeCompare(a.model_name || '');
          case 'category':    return (a.category_name || '').localeCompare(b.category_name || '');
          case 'subcategory': return (a.sub_category_name || '').localeCompare(b.sub_category_name || '');
          case 'newest':      return parseDate(b.create_audit_time) - parseDate(a.create_audit_time);
          case 'oldest':      return parseDate(a.create_audit_time) - parseDate(b.create_audit_time);
          default:            return 0;
        }
      });
  }, [models, searchTerm, statusFilter, categoryFilter, subCategoryFilter, sortBy, getEffectiveStatus]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSelectAll = () =>
    setSelectedModals(selectedModals.length === filteredAndSortedModals.length ? [] : filteredAndSortedModals.map((m) => m.id));

  const handleDeleteModal = async (id) => {
    if (!window.confirm('Are you sure you want to delete this model?')) return;
    try {
      await deleteModel(id);
      setSelectedModals((prev) => prev.filter((sid) => sid !== id));
    } catch (err) {
      console.error('Failed to delete model:', err);
    }
  };

  const handleEditModal = (modal) => { setEditingModal(modal); setShowAddModal(true); };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await toggleStatus(id, currentStatus);
      showToast(`Status updated to ${currentStatus === 'Active' ? 'Inactive' : 'Active'} successfully!`);
    } catch {
      showToast('Failed to update status.', 'error');
    }
  };

  const enrichModel = (raw, data) => {
    const cat = categories.find((c) => String(c.id) === String(raw?.category_id ?? data.categoryId));
    const sub = subCategories.find((s) => String(s.id) === String(raw?.sub_category_id ?? data.subCategoryId));
    return {
      ...raw,
      category_name: cat?.name || data.categoryName || '',
      sub_category_name: sub?.subcat_name || data.subCategoryName || '',
    };
  };

 const handleSaveModal = async (modalData) => {
  try {
    if (editingModal) {
      const updated = await updateModel(editingModal.id, modalData);
      // Only patch names if context didn't return them
      if (!updated?.category_name || !updated?.sub_category_name) {
        setModels((prev) => prev.map((m) =>
          m.id === editingModal.id ? enrichModel(updated, modalData) : m
        ));
      }
    } else {
      // addModel already calls fetchModels() internally — don't touch setModels here
      await addModel(modalData);
    }
    setEditingModal(null);
    showToast(editingModal ? 'Model updated successfully!' : 'Model created successfully!');
  } catch {
    showToast('Failed to save model.', 'error');
  }
};
  // ── Sub-components ─────────────────────────────────────────────────────────
  const CustomDropdown = ({ id, label, value, options, onChange, getLabel }) => {
    const selected = options.find((o) => String(o.value) === String(value));
    return (
      <div data-dropdown="true" className="relative">
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">{label}</label>
        <button
          type="button"
          onClick={() => setOpenDropdown(openDropdown === id ? null : id)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-left"
        >
          <span className="truncate pr-2 text-gray-700">{getLabel ? getLabel(selected) : selected?.label ?? ''}</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ${openDropdown === id ? 'rotate-180' : ''}`} />
        </button>
        {openDropdown === id && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 shadow-lg max-h-52 overflow-y-auto">
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { onChange(opt.value); setOpenDropdown(null); }}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
  String(value) === String(opt.value)
    ? 'bg-blue-600 text-white font-medium'
    : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
}`}
              >
                {getLabel ? getLabel(opt) : opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const ToggleSwitch = ({ modalId, status, disabled = false }) => (
    <label
      className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      title={disabled ? 'Parent is inactive' : status === 'Active' ? 'Set Inactive' : 'Set Active'}
    >
      <input
        type="checkbox"
        checked={status === 'Active'}
        onChange={() => !disabled && handleToggleStatus(modalId, status)}
        disabled={disabled}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-colors duration-300" />
      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 peer-checked:translate-x-5" />
    </label>
  );

  const ModalCard = ({ modal }) => {
    const parentInactive = isParentInactive(modal);
    return (
      <div className={`bg-white rounded-xl border overflow-hidden transition-all duration-300 group ${
        parentInactive ? 'border-gray-200 opacity-60 grayscale' : 'border-orange-200 hover:shadow-2xl hover:shadow-orange-200/50 hover:-translate-y-1'
      }`}>
        <div className="relative w-full h-44">
          {modal.image ? (
            <img src={modal.image} alt={modal.model_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <Layers className="w-12 h-12 text-gray-300" />
            </div>
          )}
          <div className="absolute top-3 right-3">
            <ToggleSwitch modalId={modal.id} status={modal.status} disabled={!!parentInactive} />
          </div>
          {parentInactive && (
            <span className="absolute top-3 left-3 px-2 py-0.5 bg-gray-700/80 text-white text-xs font-medium rounded-full">
              {parentInactive === 'category' ? 'Category Inactive' : 'Sub-Category Inactive'}
            </span>
          )}
        </div>
        <div className="p-4 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 mb-1 flex-wrap">
              <Tag className="w-3 h-3 text-teal-500 shrink-0" />
              <span className="text-xs text-teal-500 font-medium truncate">{modal.category_name}</span>
              <span className="text-xs text-gray-400">→</span>
              <span className="text-xs text-teal-400 truncate">{modal.sub_category_name}</span>
            </div>
            <h3 className="text-base font-semibold text-teal-800 truncate">{modal.model_name}</h3>
            <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">{modal.description}</p>
          </div>
          <div className="flex flex-col gap-2 shrink-0 mt-1">
            <button onClick={() => handleEditModal(modal)} className="p-2 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors" title="Edit">
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => handleDeleteModal(modal.id)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors" title="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ActionButtons = ({ modal, size = 14 }) => (
    <div className="flex items-center justify-end gap-2 sm:gap-3">
      <ToggleSwitch modalId={modal.id} status={modal.status} disabled={!!isParentInactive(modal)} />
      <button onClick={() => handleEditModal(modal)} className="p-1.5 sm:p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Edit">
        <Edit3 size={size} />
      </button>
      <button onClick={() => handleDeleteModal(modal.id)} className="p-1.5 sm:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
        <Trash2 size={size} />
      </button>
    </div>
  );

  const ModelImage = ({ modal, className }) =>
    modal.image ? (
      <img src={modal.image} alt={modal.model_name} className={`object-cover border border-gray-100 ${className}`} />
    ) : (
      <div className={`bg-gray-100 flex items-center justify-center ${className}`}>
        <Layers className="w-5 h-5 text-gray-400" />
      </div>
    );

  const isEmpty = filteredAndSortedModals.length === 0;
  const hasActiveFilters = searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' || subCategoryFilter !== 'all';

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <header className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-700 rounded-xl flex items-center justify-center shrink-0">
              <Layers className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-teal-800">Models</h1>
              <p className="text-sm text-gray-600 mt-0.5">Manage your product models</p>
            </div>
          </div>
          <button
            onClick={() => { setEditingModal(null); setShowAddModal(true); }}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" /> Add Model
          </button>
        </header>

        {/* Search & Filters */}
        <section className="bg-white bg-opacity-60 rounded-xl border border-orange-200 p-4 sm:p-6 mb-6 sm:mb-8 hover:shadow-2xl hover:shadow-orange-200/50 transition-all duration-300">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Search models..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-teal-600 hover:text-teal-800 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 rounded-lg px-3 py-2 text-sm border border-gray-200 bg-white sm:border-none sm:bg-transparent"
            >
              <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4 pt-4 border-t border-gray-200">
              <CustomDropdown id="status" label="Status" value={statusFilter} options={statusOptions}
                onChange={(v) => { setStatusFilter(v); setCategoryFilter('all'); setSubCategoryFilter('all'); }}
                getLabel={(o) => o ? `${o.label} (${o.count})` : ''} />
              <CustomDropdown id="category" label="Category" value={categoryFilter} options={categoryOptions}
                onChange={(v) => { setCategoryFilter(v); setSubCategoryFilter('all'); }}
                getLabel={(o) => o ? `${o.label} (${o.count})` : ''} />
              <CustomDropdown id="subcategory" label="Sub-Category" value={subCategoryFilter} options={subCategoryOptions}
                onChange={setSubCategoryFilter}
                getLabel={(o) => o ? `${o.label} (${o.count})` : ''} />
              <CustomDropdown id="sort" label="Sort By" value={sortBy} options={sortOptions} onChange={setSortBy} />

              {selectedModals.length > 0 && (
                <div className="col-span-full">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Bulk Actions ({selectedModals.length} selected)
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      className="px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-xs sm:text-sm"
                      onClick={async () => {
                        if (!window.confirm('Delete all selected models?')) return;
                        for (const id of selectedModals) {
                          try { await deleteModel(id); } catch (e) { console.error(e); }
                        }
                        setSelectedModals([]);
                      }}
                    >
                      Delete Selected
                    </button>
                    <button
                      className="px-3 py-2 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors text-xs sm:text-sm"
                      onClick={async () => {
                        for (const id of selectedModals) {
                          const m = models.find((m) => m.id === id);
                          if (m) try { await toggleStatus(id, m.status); } catch (e) { console.error(e); }
                        }
                        showToast('Bulk status updated successfully!');
                      }}
                    >
                      Toggle Status
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Models list */}
        <section>
          {isEmpty ? (
            <div className="text-center py-12 sm:py-16">
              <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-5 bg-gray-100 rounded-full flex items-center justify-center">
                <Layers className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
              </div>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3">No models found</h2>
              <p className="text-gray-500 text-sm mb-5">
                {hasActiveFilters ? 'Try adjusting your search or filter criteria.' : 'Create your first model to get started.'}
              </p>
              <button
                onClick={() => { setEditingModal(null); setShowAddModal(true); }}
                className="bg-teal-700 text-white px-5 py-2.5 rounded-xl hover:bg-teal-800 transition-colors font-medium text-sm"
              >
                Add Model
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <span className="text-xs sm:text-sm text-gray-500">
                  Showing {filteredAndSortedModals.length} of {models.length} models
                </span>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  {[{ mode: 'grid', Icon: Grid3X3 }, { mode: 'list', Icon: Layers }, { mode: 'table', Icon: Table }].map(({ mode, Icon }) => (
                    <button
                      key={mode}
                      onClick={() => setViewMode(mode)}
                      className={`p-1.5 sm:p-2 border rounded-lg transition-colors ${
                        viewMode === mode ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon size={16} className="sm:w-[18px] sm:h-[18px]" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid */}
              {viewMode === 'grid' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                  {filteredAndSortedModals.map((modal) => <ModalCard key={modal.id} modal={modal} />)}
                </div>
              )}

              {/* List */}
              {viewMode === 'list' && (
                <div className="flex flex-col gap-3">
                  {filteredAndSortedModals.map((modal) => (
                    <div
                      key={modal.id}
                      className={`bg-white border rounded-xl px-4 sm:px-5 py-3 sm:py-4 flex items-center gap-3 sm:gap-4 transition-all duration-200 ${
                        isParentInactive(modal) ? 'border-gray-200 opacity-60 grayscale' : 'border-gray-200 hover:shadow-md hover:border-orange-200'
                      }`}
                    >
                      <ModelImage modal={modal} className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 truncate text-sm sm:text-base">{modal.model_name}</h3>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          <Tag className="w-3 h-3 text-teal-500 shrink-0" />
                          <span className="text-xs text-teal-500 truncate">{modal.category_name}</span>
                          <span className="text-xs text-gray-400">→</span>
                          <span className="text-xs text-gray-500 truncate">{modal.sub_category_name}</span>
                        </div>
                      </div>
                      <ActionButtons modal={modal} size={15} />
                    </div>
                  ))}
                </div>
              )}

              {/* Table */}
              {viewMode === 'table' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                  <table className="w-full text-sm min-w-[560px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {['Image', 'Model', 'Category', 'Sub-Category', 'Actions'].map((h, i) => (
                          <th key={h} className={`px-3 sm:px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider ${i === 4 ? 'text-right' : 'text-left'} ${h === 'Sub-Category' ? 'hidden sm:table-cell' : ''}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredAndSortedModals.map((modal) => (
                        <tr key={modal.id} className={`transition-colors ${isParentInactive(modal) ? 'opacity-60 grayscale bg-gray-50' : 'hover:bg-gray-50'}`}>
                          <td className="px-3 sm:px-4 py-3">
                            <ModelImage modal={modal} className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg" />
                          </td>
                          <td className="px-3 sm:px-4 py-3 font-medium text-gray-800 max-w-[140px] truncate">{modal.model_name}</td>
                          <td className="px-3 sm:px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Tag className="w-3 h-3 text-teal-500 shrink-0" />
                              <span className="text-gray-600 truncate max-w-[100px]">{modal.category_name}</span>
                            </div>
                          </td>
                          <td className="px-3 sm:px-4 py-3 text-gray-600 hidden sm:table-cell truncate max-w-[120px]">{modal.sub_category_name}</td>
                          <td className="px-3 sm:px-4 py-3">
                            <ActionButtons modal={modal} size={14} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Drawer */}
      <ModalModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingModal(null); }}
        modal={editingModal}
        categories={categories}
        subCategories={subCategories}
        onSave={handleSaveModal}
      />

      {/* Toast */}
      <div
        style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: toast.visible ? 1 : 0,
          transform: toast.visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.97)',
          pointerEvents: toast.visible ? 'auto' : 'none',
        }}
      >
        <div className={`flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-3.5 rounded-xl shadow-lg text-xs sm:text-sm font-medium ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-teal-600 text-white'
        }`}>
          {toast.type === 'error' ? (
            <svg className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
          ) : (
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
          )}
          {toast.message}
        </div>
      </div>
    </div>
  );
};

export default ModalPage;